/**
 * Dialog para subir documentos usando TanStack Form
 * Migrado desde el patrón anterior de useState
 */
import { useState, useCallback, useEffect, useEffectEvent } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { z } from 'zod'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useDiscardGuard } from '@/components/ui/form-dialog-content'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DropZone } from './DropZone'
import { ClienteCombobox } from '@/components/ui/cliente-combobox'
import { useSubirDocumento } from '@/hooks/useDocumentos'
import { useClientes } from '@/hooks/useClientes'
import { useAuth0 } from '@/lib/auth'
import { toast } from 'sonner'
import type { CategoriaDocumento } from '@/types/database'
import { getCategoriasOptions } from '@/types/documentos'
import { Loader2 } from 'lucide-react'

// Schema de validación
const subirDocumentoSchema = z.object({
  cliente_id: z.string().min(1, 'Debe seleccionar un cliente'),
  nombre: z.string().optional(),
  categoria: z.string().min(1, 'La categoría es requerida'),
  fecha_vencimiento: z.string().nullable().optional(),
})

type SubirDocumentoFormValues = z.infer<typeof subirDocumentoSchema>

interface SubirDocumentoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clienteId?: string
  clienteNombre?: string
  onSuccess?: () => void
}

// Usar solo categorías del workspace contable (excluye jurídicas)
const categoriaOptions = getCategoriasOptions('contable')

/**
 * Helper para extraer mensaje de error de TanStack Form
 */
function getFieldError(errors: unknown[]): string | undefined {
  const first = errors[0]
  if (!first) return undefined
  if (typeof first === 'string') return first
  if (first && typeof first === 'object' && 'message' in first) {
    return (first as { message: string }).message
  }
  return undefined
}

export function SubirDocumentoDialog({
  open,
  onOpenChange,
  clienteId: clienteIdProp,
  clienteNombre,
  onSuccess,
}: SubirDocumentoDialogProps) {
  const { user } = useAuth0()
  const subirDocumentoMutation = useSubirDocumento()
  const { data: clientes = [] } = useClientes()

  const [files, setFiles] = useState<File[]>([])
  const [fileErrors, setFileErrors] = useState<string[]>([])
  // Estado local para reactividad del cliente seleccionado
  const [selectedClienteId, setSelectedClienteId] = useState(clienteIdProp || '')
  // Estado de progreso de subida (para múltiples archivos)
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 })

  // TanStack Form
  const form = useForm({
    defaultValues: {
      cliente_id: clienteIdProp || '',
      nombre: '',
      categoria: '', // No pre-seleccionar categoría, forzar selección consciente
      fecha_vencimiento: null as string | null,
    } as SubirDocumentoFormValues,
    validators: {
      onSubmit: subirDocumentoSchema,
    },
  })

  // Sincronizar clienteId del prop cuando cambia
  const syncClienteIdFromProp = useEffectEvent(() => {
    if (clienteIdProp) {
      if (!form.state.isDirty) {
        form.reset({ ...form.state.values, cliente_id: clienteIdProp }, { keepDefaultValues: true })
      }
      setSelectedClienteId(clienteIdProp)
    }
  })
  useEffect(() => {
    syncClienteIdFromProp()
  }, [clienteIdProp])

  const clienteIdFinal = clienteIdProp || selectedClienteId
  const clienteSeleccionado = clientes.find((c) => c.id === clienteIdFinal)

  const isFormDirty = useStore(form.store, (s) => s.isDirty)

  const resetForm = useCallback(() => {
    setFiles([])
    setFileErrors([])
    setSelectedClienteId(clienteIdProp || '')
    setUploadProgress({ current: 0, total: 0 })
    form.reset()
  }, [form, clienteIdProp])

  const handleClose = useCallback(() => {
    resetForm()
    onOpenChange(false)
  }, [resetForm, onOpenChange])

  const { holdClose, confirmation } = useDiscardGuard({
    isDirty: isFormDirty || files.length > 0,
    onDiscard: handleClose,
  })

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && holdClose()) return
    resetForm()
    onOpenChange(newOpen)
  }

  const handleFilesSelected = useCallback(
    (selectedFiles: File[], errors?: string[]) => {
      setFiles(selectedFiles)
      setFileErrors(errors || [])
      // Si solo hay un archivo y no se ha puesto nombre, usar el nombre del archivo
      if (selectedFiles.length === 1 && !form.state.values.nombre) {
        const fileName = selectedFiles[0].name
        const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
        form.setFieldValue('nombre', nameWithoutExtension)
      }
    },
    [form]
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!clienteIdFinal) {
      toast.error('Selecciona un cliente')
      return
    }

    if (files.length === 0) {
      toast.error('Selecciona al menos un archivo')
      return
    }

    const values = form.state.values

    if (!values.categoria) {
      toast.error('Selecciona una categoría')
      return
    }

    if (!user?.email) {
      toast.error('Error de autenticación')
      return
    }

    try {
      // Inicializar progreso
      setUploadProgress({ current: 0, total: files.length })

      // Subir cada archivo
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const documentoNombre = files.length === 1 && values.nombre ? values.nombre : file.name

        // Actualizar progreso antes de subir
        setUploadProgress({ current: i, total: files.length })

        await subirDocumentoMutation.mutateAsync({
          file,
          clienteId: clienteIdFinal,
          createdBy: user.email,
          metadatos: {
            nombre: documentoNombre,
            categoria: values.categoria as CategoriaDocumento,
            fecha_vencimiento: values.fecha_vencimiento || undefined,
          },
        })
      }

      // Marcar como completado
      setUploadProgress({ current: files.length, total: files.length })

      toast.success(
        files.length === 1
          ? 'Documento subido exitosamente'
          : `${files.length} documentos subidos exitosamente`
      )

      handleClose()
      onSuccess?.()
    } catch (error) {
      console.error('Error al subir documento:', error)
      toast.error('Error al subir el documento')
      // Resetear progreso en caso de error
      setUploadProgress({ current: 0, total: 0 })
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col p-0">
          <SheetHeader className="px-6 pt-6">
            <SheetTitle>Subir Documento</SheetTitle>
            <SheetDescription>
              {clienteNombre || clienteSeleccionado?.nombre
                ? `Sube un documento para ${clienteNombre || clienteSeleccionado?.nombre}`
                : 'Selecciona un cliente y sube un archivo'}
            </SheetDescription>
          </SheetHeader>

          {/* Contenido scrolleable */}
          <form
            id="subir-documento-form"
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto px-6 py-4 space-y-6"
          >
            {/* Selector de cliente con búsqueda (solo si no viene predefinido) */}
            {!clienteIdProp && (
              <form.Field name="cliente_id">
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="cliente">Cliente *</Label>
                    <ClienteCombobox
                      clientes={clientes}
                      value={field.state.value}
                      onValueChange={(value) => {
                        field.handleChange(value)
                        setSelectedClienteId(value)
                      }}
                      placeholder="Buscar cliente..."
                      disabled={subirDocumentoMutation.isPending}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-sm text-destructive">
                        {getFieldError(field.state.meta.errors)}
                      </p>
                    )}
                  </div>
                )}
              </form.Field>
            )}

            {/* DropZone */}
            <DropZone
              onFilesSelected={handleFilesSelected}
              multiple={true}
              disabled={subirDocumentoMutation.isPending}
              maxFiles={5}
            />

            {/* Errores de archivos */}
            {fileErrors.length > 0 && (
              <div className="text-sm text-destructive space-y-1">
                {fileErrors.map((error, i) => (
                  <p key={i}>{error}</p>
                ))}
              </div>
            )}

            {files.length > 0 && (
              <>
                {/* Categoría */}
                <form.Field name="categoria">
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor="categoria">Categoría *</Label>
                      <Select
                        value={field.state.value}
                        onValueChange={(value) => field.handleChange(value)}
                        disabled={subirDocumentoMutation.isPending}
                      >
                        <SelectTrigger id="categoria">
                          <SelectValue placeholder="Selecciona una categoría" />
                        </SelectTrigger>
                        <SelectContent>
                          {categoriaOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-sm text-destructive">
                          {getFieldError(field.state.meta.errors)}
                        </p>
                      )}
                    </div>
                  )}
                </form.Field>

                {/* Nombre personalizado (solo para un archivo) */}
                {files.length === 1 && (
                  <form.Field name="nombre">
                    {(field) => (
                      <div className="space-y-2">
                        <Label htmlFor="nombre">Nombre del documento</Label>
                        <Input
                          id="nombre"
                          value={field.state.value || ''}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          placeholder="Nombre descriptivo (opcional)"
                          disabled={subirDocumentoMutation.isPending}
                        />
                        <p className="text-xs text-muted-foreground">
                          Si no se especifica, se usará el nombre original del archivo
                        </p>
                      </div>
                    )}
                  </form.Field>
                )}

                {/* Fecha de vencimiento (opcional) */}
                <form.Field name="fecha_vencimiento">
                  {(field) => (
                    <div className="space-y-2">
                      <Label htmlFor="fechaVencimiento">Fecha de vencimiento (opcional)</Label>
                      <Input
                        id="fechaVencimiento"
                        type="date"
                        value={field.state.value || ''}
                        onChange={(e) => field.handleChange(e.target.value || null)}
                        onBlur={field.handleBlur}
                        disabled={subirDocumentoMutation.isPending}
                      />
                      <p className="text-xs text-muted-foreground">
                        Útil para poderes, contratos y constancias con fecha de expiración
                      </p>
                    </div>
                  )}
                </form.Field>
              </>
            )}
          </form>

          {/* Footer sticky con botones */}
          <SheetFooter className="px-6 py-4 border-t bg-background">
            <div className="flex justify-end gap-3 w-full">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={subirDocumentoMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                form="subir-documento-form"
                disabled={!clienteIdFinal || files.length === 0 || subirDocumentoMutation.isPending}
              >
                {subirDocumentoMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {uploadProgress.total > 1
                      ? `Subiendo ${uploadProgress.current + 1}/${uploadProgress.total}...`
                      : 'Subiendo...'}
                  </>
                ) : (
                  `Subir ${files.length > 1 ? `(${files.length})` : ''}`
                )}
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {confirmation}
    </>
  )
}
